import React, { useCallback, useEffect, useRef, useState } from "react";
import { appConfig } from "../config/appConfig";
import { api, preloadRoundArt } from "../services";
import KeepOneArena from "./KeepOneArena";
import Wordmark from "./Wordmark";

// GUESS MY TASTE — guess ONE SPECIFIC person's already-recorded taste.
// Reuses the exact KEEP ONE tap primitive (KeepOneArena) with a guess
// controller instead of a preference controller: a tap here never submits
// preference evidence, only a guess, evaluated server-side against a fixed,
// already-recorded answer. Restrained reveal: the reward is the reveal
// itself, not confetti/buzzers/arcade scoring.

export default function GuessMyTasteScreen({
  guessSessionId,
  targetHandle,
  totalRounds,
  firstRound,
  initialCorrectCount = 0,
  variant,
  reducedMotion,
  onComplete,
}) {
  const [round, setRound] = useState(firstRound);
  const [chosenSide, setChosenSide] = useState(null);
  const [reveal, setReveal] = useState(null); // { correct, revealedTitle } | null
  const [correctCount, setCorrectCount] = useState(initialCorrectCount);
  const lockRef = useRef(false);

  useEffect(() => {
    api.logEvent("guess_my_taste_round_viewed", null, { guessSessionId, sequence: round.sequence });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.sequence]);

  const arena = { left: round.left, right: round.right };

  const resolve = useCallback(
    (side) => {
      if (lockRef.current) return;
      lockRef.current = true;
      setChosenSide(side);
      const guessedId = side === "left" ? round.left.id : round.right.id;
      // guess_my_taste_prediction_submitted / _revealed are logged server-side
      // in submit_guess (source of truth for whether it actually landed).

      api
        .submitGuess(guessSessionId, round.sequence, guessedId)
        .then((res) => {
          setCorrectCount(res.correct_count);
          setReveal({ correct: res.correct, revealedTitle: res.revealed.title });
          // Warm the next matchup during the reveal beat, so the following
          // round lands instantly and the loop keeps its rhythm.
          if (!res.done) preloadRoundArt(res.next);
          const delay = reducedMotion ? 700 : appConfig.motion.advanceDelayMs + 900;
          setTimeout(() => {
            if (res.done) {
              onComplete({ correctCount: res.correct_count, totalRounds: res.total_rounds });
              return;
            }
            setRound(res.next);
            setChosenSide(null);
            setReveal(null);
            lockRef.current = false;
          }, delay);
        })
        .catch(() => {
          lockRef.current = false;
          setChosenSide(null);
        });
    },
    [round, guessSessionId, reducedMotion, onComplete]
  );

  const progress = String(round.sequence).padStart(2, "0");
  const totalStr = String(totalRounds).padStart(2, "0");

  return (
    <div className="relative flex flex-col h-[100dvh] w-full max-w-[520px] mx-auto tt-vignette">
      <header className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)]">
        <Wordmark variant="horizontal" className="h-3.5 sm:h-4 opacity-90" />
        <span data-testid="guess-round-progress" className="font-mono font-semibold tracking-[0.15em] text-xs text-subtle">
          {progress} <span className="text-white/20">/</span> {totalStr}
        </span>
      </header>

      <div className="px-5 pt-3 pb-1 text-center">
        <h1 className="font-display font-black uppercase tracking-[-0.02em] text-bone text-xl sm:text-2xl">
          Guess my taste
        </h1>
        <p className="mt-1 font-body text-[12px] tracking-wide text-silver">
          What did {targetHandle} keep?
        </p>
        <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-subtle" data-testid="guess-score">
          {String(correctCount).padStart(2, "0")} / {totalStr} · called right
        </p>
      </div>

      <main className="flex-1 min-h-0 px-4 pb-2 relative">
        <KeepOneArena
          round={arena}
          variant={variant}
          chosenSide={chosenSide}
          onChoose={resolve}
          reducedMotion={reducedMotion}
          ariaVerb="Guess"
          badgeLabel="YOUR GUESS"
        />
        {reveal && (
          <div
            data-testid="guess-reveal"
            className="absolute left-1/2 bottom-4 -translate-x-1/2 z-30 px-4 py-2 rounded-full text-center font-display font-black uppercase tracking-[0.04em] text-xs"
            style={{
              background: reveal.correct ? "var(--tt-right)" : "rgba(12,13,18,0.92)",
              color: reveal.correct ? "#05070a" : "var(--tt-bone,#f4f1ea)",
              border: reveal.correct ? "none" : "1px solid rgba(255,255,255,0.15)",
            }}
          >
            {reveal.correct ? "You called it." : `They kept ${reveal.revealedTitle}.`}
          </div>
        )}
      </main>
    </div>
  );
}
